import {useEffect, useContext, useState, useCallback} from 'react';
import {Context} from '../context';
import Button from './UI/Button';
import Section from './UI/Section';
import {clone} from 'ramda';
import {
  dollarAmount,
  getDisplayItemTextPhraseFromSlug
} from '../helperFunctions';

const selectTitleClassName = 'text-md font-semibold w-full min-w-fit mt-6 mb-2';
const selectClassName = 'text-lg w-full min-w-fit border rounded-md p-3';

function ProductSelector1() {
  const {
    account,
    addToCartPasses,
    localeText,
    content,
    setSnackBarMessage,
    getTicketName
  } = useContext(Context);

  const [nodes, setNodes] = useState([]);
  const [selections, setSelections] = useState([]);
  const [product, setProduct] = useState(null);
  const [selectedTransitCard, setSelectedTransitCard] = useState(null);

  const resetSelections = useCallback(() => {
    setSelections([]);

    const selection1 = nodes.find(node => node.key === 'root');
    if (selection1) {
      setSelections([selection1]);
    }

    setProduct(null);
  }, [nodes]);

  useEffect(() => {
    resetSelections();
  }, [nodes, resetSelections, selectedTransitCard]);

  useEffect(() => {
    const fetchedNodes = content?.menu?.nodes;
    if (fetchedNodes?.length > 0) {
      setNodes(fetchedNodes);
    }
  }, [content]);

  const getNode = useCallback(
    nodeKey => nodes.find(node => node.key === nodeKey),
    [nodes]
  );

  const add = () => {
    addToCartPasses({
      id: product.id,
      key: product.key,
      quantity: 1,
      price: product.price,
      uuid: selectedTransitCard.uuid
    });

    setSnackBarMessage(
      localeText(
        'rw_purchasepasses_text_item_added_to_cart',
        'Item added to cart'
      )
    );

    resetSelections();
  };

  const handleTransitCardSelection = e => {
    const selectedCardUuid = e.target.value;

    const selectedCard = account?.transitAccounts?.[0]?.transitCards.find(
      card => card.uuid === selectedCardUuid
    );
    if (selectedCard) {
      setSelectedTransitCard(selectedCard);
    } else {
      setSelectedTransitCard(null);
    }
  };

  const handleSelection = (e, index) => {
    const selectedNode = getNode(e.target.value);

    const curSelections = oldSelections =>
      clone(oldSelections.slice(0, index + 1));

    if (e.target.value === 'default') {
      setSelections(prev => curSelections(prev));
      setProduct(null);
      return;
    }

    // while we are still navigating nodes
    if (selectedNode?.items) {
      setSelections(prev => [...curSelections(prev), selectedNode]);
      setProduct(null);
      return;
    }

    // if we arrive at a product node
    setProduct(selectedNode);
  };

  return (
    <Section
      heading={localeText('rw_purchasepasses_header', 'Product Selector')}
    >
      {account?.transitAccounts?.[0]?.transitCards && (
        <div className='w-full flex flex-col'>
          <label
            htmlFor={`menu-transit-cards`}
            className={selectTitleClassName}
          >
            Choose Transit Card:
          </label>
          <select
            name='transit-cards'
            id={`menu-transit-cards`}
            className={selectClassName}
            onChange={e => handleTransitCardSelection(e)}
          >
            <option value='default'> -- Select Transit Card -- </option>
            {account?.transitAccounts?.[0]?.transitCards.map(transitCard => (
              <option key={transitCard.uuid} value={transitCard.uuid}>
                {transitCard.nickname}
              </option>
            ))}
          </select>
        </div>
      )}
      {selectedTransitCard &&
        selections.map((selection, index) => {
          return (
            <div key={selection.key} className='w-full flex flex-col'>
              <label
                htmlFor={`menu-${selection.key}`}
                className={selectTitleClassName}
              >
                Choose
                {selection?.displayItemTitle
                  ? ` ${selection.displayItemTitle}`
                  : ''}
                :
              </label>
              <select
                name={selection.key}
                id={`menu-${selection.key}`}
                className={selectClassName}
                onChange={e => handleSelection(e, index)}
              >
                <option value='default'>
                  {' '}
                  -- Select {selection.displayItemTitle} --{' '}
                </option>
                {selection?.items?.map(itemKey => (
                  <option key={itemKey} value={itemKey}>
                    {getDisplayItemTextPhraseFromSlug(
                      localeText,
                      content,
                      getNode(itemKey)?.displayItem
                    )}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      {selectedTransitCard && product && (
        <div className='flex flex-col my-6'>
          <div className='text-lg font-semibold'>
            {getTicketName(product.id)}
          </div>
          <div className='text-lg font-bold'>{dollarAmount(product.price)}</div>
          <Button className='w-fit self-end' onClick={add}>
            {localeText('rw_purchasepasses_button_add_to_cart', 'Add to Cart')}
          </Button>
        </div>
      )}
    </Section>
  );
}

export default ProductSelector1;
